import GifffButton from "./button";
import { Table, TableBody, TableCell, TableRow } from "../ui/table";
import { ArrowRightCircle, Circle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Accordion as FAQAccordion,
  AccordionContent as FAQAccordionContent,
  AccordionItem as FAQAccordionItem,
  AccordionTrigger as FAQAccordionTrigger,
} from "../ui/faq-accordion";
import Image from "next/image";
import { cn } from "@/utils/cn";
import { DashboardData } from "@/pages/gifff/dashboard";
import mixpanel from "@/utils/mixpanel";
import useKey from "@/hooks/use-key";
import { Button } from "../ui/button";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { YouTubePlayer } from "@/pages/[id]/redirect/payment-page";

const gifffDescription = [
  <div>
    Financial freedom doesn’t come just from earning — it comes from{" "}
    <b>learning, sharing, and growing together.</b>
  </div>,
  <div>
    That’s why we built <b>TRS</b>, a system designed to reward you for inviting
    your friends and family to start their trading journey.
  </div>,
  <div>
    Every referral earns you <b>coins</b>, which you can redeem for cash
    rewards, premium resources, and exclusive merchandise.
  </div>,
  <div>
    The more you share, the more you climb up the leaderboard, unlocking{" "}
    <b>real money and prizes.</b>
  </div>,
  <div>
    With TRS, you’re not just earning — you’re{" "}
    <b>building a movement of traders.</b>
  </div>,
];

const scheduleData = [
  {
    title: "Swing trading for your Financial freedom",
    items: [
      {
        label: "Opening and onboarding for festival",
        description: "Join the 4 day event toEarn up to ₹10,000",
      },
      {
        label: "Live Classes with Swing trading experts",
        description: "Live sessions with Swing trading experts",
      },
    ],
  },
  {
    title: "All day Intraday",
    items: [
      {
        label: "Trading session",
        description: "Trading session with Swing trading experts",
      },
      {
        label: "Best intraday Indicator",
        description: "Best intraday indicator",
      },
    ],
  },
  {
    title: "Forex and Commodities",
    items: [
      {
        label: "Commodity trading class",
        description: "Trading session with Swing trading experts",
      },
      { label: "Trading contest", description: "Trading contest" },
      {
        label: "Forex trading funded account",
        description: "Forex trading funded account",
      },
    ],
  },
  {
    title: "A day with Institutional Traders",
    items: [
      {
        label: "Trading session",
        description: "Trading session with Swing trading experts",
      },
    ],
  },
];

const faqs = [
  {
    question: "What is this platform about?",
    answer:
      "It’s a learning and earning ecosystem designed to help you master trading and investing while participating in engaging challenges, live sessions, and referral-based rewards.",
  },
  {
    question: "How do I get started?",
    answer:
      "Just sign up on our platform and unlock access to expert-led classes, workshops, and resources that guide you step by step toward financial freedom.",
  },
  {
    question: "What kind of training is provided?",
    answer:
      "We offer a mix of live sessions, expert workshops, market insights, strategies, and ready-to-use trading tools to help you trade smarter and build long-term wealth.",
  },
  {
    question: "Is it suitable for beginners?",
    answer:
      "Absolutely. The platform is structured for all levels—whether you’re just starting your trading journey or looking to sharpen advanced strategies, you’ll find valuable guidance and tools here.",
  },
];

export default function Gifff({
  switchTab,
  className,
  dashboardData,
}: {
  switchTab: (tab: string) => void;
  className?: string;
  dashboardData: DashboardData | null;
}) {
  return (
    <section className={cn("grid gap-8 p-2", className)}>
      <div className="px-4 grid gap-3">
        <h1 className="text-2xl mt-6 font-semibold mx-auto">
          #IndiaKaregaTrade
        </h1>
        <YouTubePlayer
          src="https://youtube.com/embed/g_IhyUunzgg?autoplay=1&mute=1&loop=1&playlist=g_IhyUunzgg"
          className="!aspect-square"
          btnClass="!bg-amber-300"
        />
        <GifffButton
          onClick={() => {
            const userDataAsJSON = JSON.parse(
              localStorage.getItem("tfu-user-auth") ?? "{}",
            );

            mixpanel.track("Earn_upto_Rs10000", {
              position: "top",
              name: userDataAsJSON.name,
              email: userDataAsJSON.email,
            });
            switchTab("refer");
          }}
          size={"lg"}
          className="text-xl py-3 font-normal h-auto rounded-lg bg-amber-300/90 text-black "
        >
          Earn upto ₹10,000
        </GifffButton>
      </div>

      <CustomGiffButton />

      <WhatIsGifff />

      {/* Detailed 4 day schedule */}
      <Image
        src={"/gifff/new-gifff-schedule.png"}
        width={1200}
        height={1200}
        unoptimized
        alt="schedule"
      />
      <div className="sticky bottom-0 grid place-items-center bg-gradient-to-t backdrop-blur-sm from-white/50 to-white/10 z-20">
        <GifffButton
          onClick={() => {
            const userDataAsJSON = JSON.parse(
              localStorage.getItem("tfu-user-auth") ?? "{}",
            );
            mixpanel.track("Earn_upto_Rs10000", {
              position: "mid",
              name: userDataAsJSON.name,
              email: userDataAsJSON.email,
            });
            switchTab("refer");
          }}
          size={"lg"}
          className="text-xl w-full py-3 font-normal h-auto rounded-lg my-4 bg-amber-300/90 text-black "
        >
          Earn upto ₹10,000
        </GifffButton>
      </div>
      <FAQs />
    </section>
  );
}

const Schedule = () => {
  return (
    <div className="px-4 grid gap-3 place-items-center">
      <h2 className="text-xl font-extrabold text-sky-950">
        Detailed 4 day schedule
      </h2>
      <div className="h-1 w-24 bg-amber-300/90" />

      <div className="relative grid gap-2">
        {scheduleData.map((day, index) => (
          <div key={index} className="relative space-y-2">
            {index !== scheduleData.length - 1 && (
              <div className="absolute left-0 top-8 h-full w-0.5 bg-amber-300/90" />
            )}

            <div className="absolute -left-3 top-3 size-6 bg-amber-300 rounded-full z-20" />
            <h3 className="text-lg pl-6 font-bold text-sky-950">
              Day {index + 1}: {day.title}
            </h3>
            <Accordion type="multiple" className="pl-6 space-y-2">
              {day.items.map((item, itemIndex) => (
                <AccordionItem
                  key={itemIndex}
                  value={item.label}
                  className="border rounded-xl p-4"
                >
                  <AccordionTrigger className="border-none p-0 hover:no-underline">
                    {item.label}
                  </AccordionTrigger>
                  <AccordionContent className="p-0 mt-4">
                    {item.description}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FAQs = () => {
  return (
    <div className="px-4 grid gap-3 place-items-center">
      <h2 className="text-xl font-extrabold text-sky-950">
        Frequently Asked Questions
      </h2>
      <div className="h-1 w-24 bg-amber-300" />

      <FAQAccordion type="multiple" className="w-full space-y-4">
        {faqs.map((faq, index) => (
          <FAQAccordionItem
            key={index}
            value={faq.question}
            className="border bg-gray-100 rounded-xl p-4"
          >
            <FAQAccordionTrigger
              onClick={() => {
                const userDataAsJSON = JSON.parse(
                  localStorage.getItem("tfu-user-auth") ?? "{}",
                );
                mixpanel.track("faq_viewed", {
                  question: faq.question,
                  ...(userDataAsJSON.email && {
                    phone: userDataAsJSON.phone,
                    name: userDataAsJSON.name,
                  }),
                });
              }}
              className="border-none bg-transparent p-0 hover:no-underline"
            >
              Q. {faq.question}
            </FAQAccordionTrigger>
            <FAQAccordionContent className="p-0 mt-4">
              {faq.answer}
            </FAQAccordionContent>
          </FAQAccordionItem>
        ))}
      </FAQAccordion>
    </div>
  );
};

export const WhatIsGifff = () => {
  return (
    <div className="px-4 grid gap-3 place-items-center z-30 bg-white">
      <h2 className="text-xl font-extrabold text-sky-950">What is TRS?</h2>
      <div className="h-1 w-24 bg-amber-300" />

      <div className="bg-amber-300/10  border-2 rounded-xl p-4">
        <Table className="p-0">
          <TableBody>
            {gifffDescription.map((description, index) => (
              <TableRow key={index} className="hover:bg-transparent">
                <TableCell className="flex items-start gap-2 p-2">
                  <ArrowRightCircle size={24} />
                  <span className="flex-1">{description}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const CustomGiffButton = () => {
  const buttonData = useKey<{
    label: string;
    url: string;
    title: string;
    show: boolean;
    topTitle: string;
    live: boolean;
  }>("gifff-button", { json: true });

  if (!buttonData || !buttonData.show) return null;

  return (
    <section className="bg-gradient-to-br p-3 grid gap-4 place-items-center from-amber-300/25 via-white to-amber-300/25 rounded-xl mx-4">
      <div className="flex w-full items-center justify-between">
        <span className="font-semibold">{buttonData.topTitle}</span>
        {buttonData.live && (
          <span className="font-semibold text-red-500">
            <Circle
              size={12}
              color="red"
              className="fill-red-500 size-1 animate-ping mr-2 inline"
            />
            LIVE
          </span>
        )}
      </div>
      <h3>{buttonData.title}</h3>
      <Button
        asChild
        className="bg-amber-300 !text-black hover:!bg-amber-300  hover:!no-underline hover:!text-black rounded-lg w-full h-12 text-xl"
        size={"lg"}
      >
        <Link href={buttonData.url} className="!text-black">
          {buttonData.label}
        </Link>
      </Button>
    </section>
  );
};
